import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { axiosInstance } from './config';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import Home from './pages/home/Home';
import Profile from './pages/profile/Profile';
import Adminpage from './pages/admin/Admin';
import AdminMusic from './pages/adminmusic/AdminMusic';
import AdminDashboard from './pages/admindashboard/AdminDashboard';
import AdminUsers from './pages/adminusers/AdminUsers';
import AdminUserDetail from './pages/adminuser/AdminUserDetail';
import ClipReviewSandbox from './pages/ClipReviewSandbox/ClipReviewSandbox';
import WhopClickTestSandbox from './pages/WhopClickTestSandbox/WhopClickTestSandbox';
import AuthSuccess from './pages/auth/AuthSuccess';
import Appearancepage from './pages/appearance/Appearance';
import SignUp from './pages/signup/Signup';
import Signupstarter from './pages/signup/Signupstarter';
import Signuppro from './pages/signup/Signuppro';
import Signuppremium from './pages/signup/Signuppremium';
import Verifyemail from './pages/verifyemailsent/Verifyemailsent';
import Emailverified from './pages/emailverified/Emailverified';
import { AuthContext } from './context/AuthContext';
import ForgotPassword from './components/forgotpassword/Forgotpassword';
import PricingPage from './pages/pricing/PricingPage';
import About from './pages/about/about';
import Press from './press/press';
import AgencyOnboardingPage from './pages/AgencyOnboarding/AgencyOnboardingPage';
import CookieNotice from './pages/cookienotice/cookienotice';
import Terms from './pages/termsandconditions/termsandconditions';
import Subscriptions from './pages/subscriptions/Subscriptions';
// Streaming page removed — not a streaming app
// import YzyStreaming from './pages/yzystreaming/yzystreaming';
import ChatPopout from './pages/chatpopout/chatpopout';
import Notifications from './pages/notifications/Notifications';
import SuperChats from './pages/superchats/SuperChats';
import ForYouPage from './pages/ForYouPage/ForYouPage';
import ContactSpirit from './pages/ContactSpirit/ContactSpirit';
import ClipPage from './pages/clip/ClipPage';
import SearchPage from './pages/search/SearchPage';
import NotificationsPage from './pages/notificationstab/NotificationsPage';
import MobileBottomBar from './components/mobileBottomBar/MobileBottomBar';
import AuthPage from './pages/auth/AuthPage';
import MePage from './pages/me/MePage';
import AffiliatePortal from './pages/affiliates/AffiliatePortal';
import ClipperApplyPage from './pages/ClipperApply/ClipperApplyPage';
import CalendarPage from './pages/calendar/CalendarPage';
import ClipEditor from './pages/edit/ClipEditor';
import UpgradePage from './pages/upgrade/UpgradePage';
import TweetToReel from './pages/TweetToReel/TweetToReel';
import CampaignsPage from './pages/CampaignsPage/CampaignsPage';
import RumblePitchPage from './pages/RumblePitch/RumblePitchPage';
import PitchAnalytics from './pages/RumblePitch/PitchAnalytics';
import CheckoutHandoff from './pages/checkout/CheckoutHandoff';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import { identifyUser } from './utils/analytics';
import { applyCountryLanguage } from './utils/region';

function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#1a1a1a', color: 'rgba(255,255,255,0.85)',
      borderBottom: '1px solid rgba(255,106,0,0.35)',
      padding: '10px 16px', textAlign: 'center', fontSize: '13px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff6a00" strokeWidth="2" strokeLinecap="round">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/>
      </svg>
      No internet connection
    </div>
  );
}

function useIsMobile(breakpointPx = 900) {
  const compute = () => {
    if (typeof window === 'undefined') return false;
    const mm = window.matchMedia("(max-width: " + breakpointPx + "px)").matches;
    const uaMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    return mm || uaMobile;
  };
  const [isMobile, setIsMobile] = useState(compute);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mm = window.matchMedia("(max-width: " + breakpointPx + "px)");
    const onChange = () => setIsMobile(compute());
    mm.addEventListener?.('change', onChange);
    window.addEventListener('orientationchange', onChange);
    window.addEventListener('resize', onChange);
    return () => {
      mm.removeEventListener?.('change', onChange);
      window.removeEventListener('orientationchange', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, [breakpointPx]);
  return isMobile;
}

function CreditSuccessVerifier() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const verify = searchParams.get('verify');
    if (!sessionId || verify !== '1' || !user) return;

    axiosInstance.get(`/billing/credits/success?session_id=${sessionId}`)
      .then(res => {
        if (res.data?.ok && !res.data?.already_applied) {
          toast.success(`${res.data.granted} credits added to your account!`);
        }
      })
      .catch(() => {}) // webhook may have already handled it
      .finally(() => {
        // Clean URL params
        searchParams.delete('session_id');
        searchParams.delete('verify');
        searchParams.delete('credits_success');
        setSearchParams(searchParams, { replace: true });
      });
  }, []); // run once on mount

  return null;
}

function RootLayout() {
  const { pathname } = useLocation();
  const isMobile = useIsMobile(900);
  const HIDE_PREFIXES = useMemo(
    () => [
      '/chatpopout',
      '/login',
      '/signup',
      '/forgot-password',
      '/emailsent',
      '/auth/verify',
      '/auth-success',
      '/rumble-pitch',
      '/checkout',
    ],
    []
  );
  const shouldHideBar = (p) =>
    HIDE_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix));
  const showBar = isMobile && !shouldHideBar(pathname);
  const BOTTOM_BAR_HEIGHT = 68;
  const paddingBottom = showBar
    ? "calc(" + BOTTOM_BAR_HEIGHT + "px + env(safe-area-inset-bottom, 0px))"
    : 0;
  return (
    <>
      <OfflineBanner />
      <CreditSuccessVerifier />
      <div style={{ minHeight: '100vh', paddingBottom }}>
        <Outlet />
      </div>
      {showBar && <MobileBottomBar />}
    </>
  );
}

// Capture ?via= referral param BEFORE React renders (router strips query params on redirect).
// First-touch wins: persist to localStorage AND a first-party cookie scoped to
// .clipbait.ai (90-day) so attribution survives across subdomains and is not
// overwritten by a later visit.
(function captureReferralParam() {
  const via = new URLSearchParams(window.location.search).get("via");
  if (!via) return;
  try {
    if (!localStorage.getItem("referral_via")) localStorage.setItem("referral_via", via);
  } catch { /* ignore */ }
  try {
    // first-touch: don't overwrite an existing cb_via cookie
    const has = document.cookie.split("; ").some((c) => c.startsWith("cb_via="));
    if (!has) {
      const host = window.location.hostname || "";
      const onClipbait = host === "clipbait.ai" || host.endsWith(".clipbait.ai");
      const domain = onClipbait ? "; domain=.clipbait.ai" : "";
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      const maxAge = 90 * 24 * 60 * 60; // 90 days
      document.cookie = `cb_via=${encodeURIComponent(via)}; path=/; max-age=${maxAge}${domain}${secure}; SameSite=Lax`;
    }
  } catch { /* ignore */ }
})();

// Redirect that preserves query params (so ?via= reaches /login for Rewardful)
function NavigateWithParams({ to }) {
  const { search } = useLocation();
  return <Navigate to={to + search} replace />;
}

function App() {
  const { user, dispatch } = useContext(AuthContext);
  const isMobileToast = typeof window !== 'undefined' && window.innerWidth <= 768;

  // No language picked yet + English browser → use the visitor's country language.
  useEffect(() => { applyCountryLanguage(); }, []);

  // Attach PostHog events to the logged-in user.
  useEffect(() => { if (user) identifyUser(user); }, [user]);

  // Auto-logout when refresh token is also expired
  useEffect(() => {
    const handler = () => dispatch({ type: "LOGOUT" });
    window.addEventListener("auth:sessionExpired", handler);
    return () => window.removeEventListener("auth:sessionExpired", handler);
  }, [dispatch]);

  return (
    <Router>
      <Routes>
        <Route path="/auth-success" element={<AuthSuccess />} />
        {/* Streaming route removed — not a streaming app */}
        <Route element={<RootLayout />}>
          <Route path="/:streamerKey/:vodId" element={<ClipPage />} />
          <Route path="/contact" element={<ContactSpirit />} />
          <Route path="/agency-onboarding" element={<AgencyOnboardingPage />} />
          {/* Public clipper/editor recruitment funnel → routes to Whop. No sign-in. */}
          <Route path="/forms" element={<ClipperApplyPage />} />
          {/* Protected route - requires authentication */}
          <Route 
            path="/home" 
            element={user ? <ForYouPage /> : <Navigate to="/login" />} 
          />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/cookie-notice" element={<CookieNotice />} />
          <Route path="/press" element={<Press />} />
          <Route path="/pricing" element={user ? <PricingPage /> : <NavigateWithParams to="/login" />} />
          <Route path="/checkout" element={<CheckoutHandoff />} />
          <Route path="/chatpopout" element={<ChatPopout />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/notificationspage" element={<NotificationsPage />} />
          <Route path="/user/plan" element={<Navigate to="/pricing" replace />} />
          <Route
            path="/subscriptions"
            element={user ? <Subscriptions /> : <Navigate to="/login" />}
          />
          <Route
            path="/notifications"
            element={user ? <Notifications /> : <Navigate to="/login" />}
          />
          <Route path="/superchats/:streamer?" element={<SuperChats />} />
          <Route path="/analytics" element={user ? <AnalyticsPage /> : <Navigate to="/login" />} />
          <Route path="/admin" element={user ? <Adminpage /> : <Navigate to="/login" />} />
          <Route path="/admin/music" element={user ? <AdminMusic /> : <Navigate to="/login" />} />
          <Route path="/admin/dashboard" element={user ? <AdminDashboard /> : <Navigate to="/login" />} />
          <Route path="/admin/users" element={user ? <AdminUsers /> : <Navigate to="/login" />} />
          <Route path="/admin/users/:id" element={user ? <AdminUserDetail /> : <Navigate to="/login" />} />
          <Route path="/admin/clip-review-sandbox" element={user ? <ClipReviewSandbox /> : <Navigate to="/login" />} />
          <Route path="/admin/whop-click-test" element={user ? <WhopClickTestSandbox /> : <Navigate to="/login" />} />
          <Route
            path="/admin/appearance"
            element={user ? <Appearancepage /> : <Navigate to="/login" />}
          />
          <Route path="/login" element={user ? <Navigate to="/home" /> : <AuthPage />} />
          <Route path="/signup" element={user ? <Navigate to="/home" /> : <AuthPage />} />
          <Route path="/signup/starter" element={<Signupstarter />} />
          <Route path="/signup/pro" element={<Signuppro />} />
          <Route path="/signup/premium" element={<Signuppremium />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/signup/user/:username" element={<SignUp />} />
          <Route path="/emailsent/success" element={<Verifyemail />} />
          <Route path="/auth/verify/:token" element={<Emailverified />} />
          <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
          <Route path="/me" element={user ? <MePage /> : <Navigate to="/login" />} />
          {/* Public: the affiliate portal handles its own Google sign-in +
              access-password gate — no bounce to the app login wall. */}
          <Route path="/affiliates" element={<AffiliatePortal />} />
          <Route path="/calendar" element={user ? <CalendarPage /> : <Navigate to="/login" />} />
          <Route path="/tweet-to-reel" element={user ? <TweetToReel /> : <Navigate to="/login" />} />
          <Route path="/edit/:jobId/:clipIndex" element={user ? <ClipEditor /> : <Navigate to="/login" />} />
          <Route path="/upgrade" element={user ? <UpgradePage /> : <Navigate to="/login" />} />
          <Route path="/campaigns" element={user ? <CampaignsPage /> : <Navigate to="/login" />} />
          {/* Pitch pages — public, self-gated with password */}
          <Route path="/rumble-pitch" element={<RumblePitchPage />} />
          <Route path="/rumble-pitch/analytics" element={<PitchAnalytics />} />
          <Route path="/:username" element={<Profile />} />
          {/* Protected route - redirect / to /home, which requires auth */}
          <Route
            path="/"
            element={user ? <Navigate to="/home" /> : <NavigateWithParams to="/login" />}
          />
        </Route>
      </Routes>
      <Toaster
        position={isMobileToast ? "bottom-center" : "top-left"}
        containerStyle={{
          zIndex: 2147483647,
          ...(isMobileToast && {
            bottom: 'calc(68px + env(safe-area-inset-bottom, 0px) + 170px)',
            top: 'auto',
          }),
        }}
        toastOptions={{
          duration: 3000,
          style: {
            background: '#111',
            color: '#fff',
            borderRadius: '12px',
            padding: '10px 16px',
            fontSize: 14,
            whiteSpace: 'nowrap',
            maxWidth: '90vw',
          },
          error: {
            duration: 4000,
          },
        }}
      />
    </Router>
  );
}

export default App;